import { ExecutionRequestStatus } from "@/lib/interfaces and Enums/ENUMExecutionRequestStatus";

/**
 * Returns the iceberg percentage for a CP (releaseSize * 100, capped at 100),
 * or null when the cell should render blank (no releaseSize set).
 *
 * A releaseSize >= 1 still returns 100 so multi-CP tables don't show a blank
 * cell next to populated ones — the cell is rendered but not editable
 * (100% is the max, can't go down). Column visibility is gated separately
 * by `cpHasIceberg` / `tradeHasIceberg` in `colDefsShared`.
 */
export function calculateIcebergPercentForER(cp: any): number | null {
  if (!cp) return null;
  const releaseSize = cp.releaseSize;
  if (releaseSize === null || releaseSize === undefined || releaseSize === "") {
    return null;
  }
  const value = Number(releaseSize);
  if (isNaN(value)) return null;
  return Math.min(value * 100, 100);
}

/**
 * Statuses the Iceberg cell can be edited in. Sourced from LQC-922:
 * - RFQ: Sent, ReSent, Acknowledged, Recalled, OffQuote, Quoted, ReQuoteRequested
 * - Order: Sent, Received, Recalled, ReSent
 *
 * Both lists are checked against ExecutionRequestStatus on the CP row.
 */
export const ICEBERG_EDITABLE_STATUSES_RFQ: ExecutionRequestStatus[] = [
  ExecutionRequestStatus.Sent,
  ExecutionRequestStatus.Received, // Check this?
  ExecutionRequestStatus.ReSent,
  ExecutionRequestStatus.Acknowledged, // Check this?
  ExecutionRequestStatus.Recalled,
  ExecutionRequestStatus.OffQuote,
  ExecutionRequestStatus.Quoted,
  ExecutionRequestStatus.ReQuoteRequested,
];

export const ICEBERG_EDITABLE_STATUSES_ORDER: ExecutionRequestStatus[] = [
  ExecutionRequestStatus.Sent,
  ExecutionRequestStatus.Received,
  ExecutionRequestStatus.Recalled,
  ExecutionRequestStatus.ReSent,
  ExecutionRequestStatus.Working,
  ExecutionRequestStatus.Acknowledged,
];

/**
 * LQC-1040: once a trade is sent, the iceberg release size can only be
 * increased, never reduced. Returns true when `newValue` would reduce the
 * current value. Equal values are NOT a reduction (and are typically a no-op
 * upstream anyway). When there's no current value to compare against, no
 * reduction is possible — returns false.
 */
export function isIcebergReduction(
  newValue: number,
  currentValue: number | null | undefined,
): boolean {
  if (currentValue === null || currentValue === undefined) return false;
  if (isNaN(currentValue) || isNaN(newValue)) return false;
  return newValue < currentValue;
}

/**
 * Pure editability predicate. The cell is editable when:
 *   - the CP has a releaseSize set, AND
 *   - the percent is below 100 (100% is the max — can't go down), AND
 *   - the CP row's status is in the allowed list for the trade type.
 *
 * Use this directly from the renderer (where live `thisTrade` from
 * tradeDataStore is available, so the pill styling stays reactive). The AG
 * Grid `editable` callback uses the `isIcebergCellEditable` wrapper below.
 */
export function isIcebergEditableForCP(
  cp: any,
  fullTradeData: any,
): boolean {
  const percent = calculateIcebergPercentForER(cp);
  if (percent === null) return false;
  if (percent >= 100) return false;

  const status = cp?.status as ExecutionRequestStatus | undefined;
  if (!status) return false;

  const isOrder = fullTradeData?.type === "Order";

  const allowed = isOrder
    ? ICEBERG_EDITABLE_STATUSES_ORDER
    : ICEBERG_EDITABLE_STATUSES_RFQ;

  return allowed.includes(status);
}

/**
 * AG Grid `editable` predicate for the Iceberg column. CP rows have no `type`
 * field and `params.context` is reserved for `collapsedFillBlocks`, so the
 * trade type is read from `params.colDef.cellRendererParams.fullTradeData`
 * (where it's actually populated by the colDef factory).
 */
export function isIcebergCellEditable(params: any): boolean {
  const cp = params?.data;
  const fullTradeData =
    params?.colDef?.cellRendererParams?.fullTradeData ??
    params?.context?.fullTradeData;
  return isIcebergEditableForCP(cp, fullTradeData);
}
